<template>
  <div class="login">
    <section class="banner" />

    <section>
      <a href="https://www.cardladder.com">
        <full-logo pro />
      </a>

      <div class="form-container">
        <h1 class="secondary-font">
          Login
        </h1>

        <form @submit.prevent="login">
          <auth-form v-model="form" />

          <btn
            type="submit"
            block
            :loading="submitting"
            loading-text="Logging in"
          >
            Submit
          </btn>
        </form>

        <line-separator>OR</line-separator>

        <social-auth type="login" @complete="complete" />

        <div class="links">
          <a class="link" @click="$router.push({ path: '/forgot-password', query: $route.query })">Forgot your password?</a>
          <span>|</span>
          <a class="link" @click="$router.push({ path: '/signup', query: $route.query })">Create an account</a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';

import FullLogo from '@cardladder/shared/components/FullLogo';
import { getFirebaseErrorMessage, getRecaptchaErrorMessage } from '@cardladder/shared/utils/auth.util';

import AuthForm from './AuthForm';
import SocialAuth from './SocialAuth';

export default {
  components: {
    FullLogo,
    AuthForm,
    SocialAuth,
  },

  data() {
    return {
      form: {
        email: '',
        password: '',
      },
      submitting: false,
    };
  },

  watch: {
    '$route.query.email': {
      handler(value) {
        if (value) {
          this.form.email = value;

          const query = { ...this.$route.query };
          delete query.email;
          this.$router.replace({ query });
        }
      },
      immediate: true,
    },
  },

  methods: {
    async login() {
      this.submitting = true;
      this.$toasted.clear();

      let token;
      try {
        // eslint-disable-next-line no-undef
        if (typeof grecaptcha === 'undefined' || !grecaptcha.enterprise) {
          throw new Error('grecaptcha is not available');
        }

        // eslint-disable-next-line no-undef
        token = await grecaptcha.enterprise.execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, { action: 'login' });
      } catch (e) {
        console.error('login:recaptcha_execute', e);
        this.$toasted.error(getRecaptchaErrorMessage(e));
        this.submitting = false;
        return;
      }

      try {
        await firebase.functions().httpsCallable('httpVerifyRecaptcha')({ token, action: 'login' });
      } catch (e) {
        console.error('login:recaptcha_verify', e.code, e.message, e);
        this.$toasted.error(getRecaptchaErrorMessage(e));
        this.submitting = false;
        return;
      }

      try {
        await firebase.auth().signInWithEmailAndPassword(this.form.email.trim(), this.form.password);
        await this.$store.dispatch('boot', firebase.auth().currentUser);

        this.complete();
      } catch (e) {
        console.error('login:auth', e.code, e.message, e);
        this.$toasted.error(getFirebaseErrorMessage(e.code));
      }

      this.submitting = false;
    },

    complete() {
      this.$router.push({ path: '/dashboard', query: this.$route.query }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.login {
  min-height: 100%;

  @media($medium-up) {
    display: grid;
    grid-template-columns: 1.75fr 1fr;
  }

  > section {
    padding: $gp;
    display: flex;
    flex-direction: column;

    @media($medium-up) {
      padding: $gp * 2;
    }

    &:first-of-type {
      background-image: url('@/assets/login-banner.png');
      background-size: cover;

      @media($medium-down) {
        display: none;
      }
    }
  }
}

.form-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 450px;
  margin: $gp * 2 auto;

  form,
  .social-auth {
    width: 100%;
  }
}

.links {
  margin-top: $gp;
  font-size: 12px;

  a {
    cursor: pointer;
    padding: 0 4px;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
